import { useEffect, useState, useCallback } from "react";
import { Link, useOutletContext } from "react-router-dom";
import api from "@/lib/api";
import { formatCurrency, formatNumber, formatRelative } from "@/lib/format";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import {
  TrendingUp,
  Clock,
  Target,
  AlertTriangle,
  CheckCircle2,
  Zap,
  ArrowRight,
  MessagesSquare,
  Link2,
  Smartphone,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import SimulateModal from "@/components/SimulateModal";
import WelcomeModal from "@/components/WelcomeModal";
import {
  PeriodFilter,
  HeroKPI,
  FunnelPanel,
  StepPerformance,
  HealthPanel,
  AvgMetrics,
  TopIntegrations,
  TopProducts,
  MessagesPanel,
  HeatmapPanel,
  RecentTransactions,
  UpcomingJobs,
} from "@/components/dashboard/Insights";

function Metric({ label, value, sub, icon: Icon, accent = "zinc" }) {
  const accents = {
    primary: "text-cp-primary-light",
    accent: "text-cp-accent",
    amber: "text-cp-warning",
    zinc: "text-slate-100",
  };
  return (
    <div className="cp-panel p-5 transition-colors hover:border-white/10">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">
          {label}
        </div>
        {Icon && <Icon className="h-4 w-4 text-slate-600" />}
      </div>
      <div
        className={`font-heading text-3xl md:text-[32px] font-bold tabular-nums tracking-tight ${accents[accent]}`}
      >
        {value}
      </div>
      {sub && <div className="text-xs text-slate-500 mt-1.5">{sub}</div>}
    </div>
  );
}

function OnboardingChecklist({ status, onActivateRecovery, activating }) {
  const percent = status?.percent || 0;
  const icons = {
    whatsapp: Smartphone,
    checkout: Link2,
    automation: Zap,
  };
  const subtitles = {
    whatsapp: "Conecte seu numero para enviar as mensagens",
    checkout: "Cole o webhook no seu checkout",
    automation: "Ligue o fluxo padrao de recuperacao",
  };
  const states = {
    not_started: "Nao iniciado",
    in_progress: "Em andamento",
    completed: "Concluido",
  };
  return (
    <div className="cp-card relative overflow-hidden border-cp-primary/25 p-6 md:p-8">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-cp-primary-light font-semibold mb-1">
            Onboarding
          </div>
          <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tighter">
            Conecte seu WhatsApp, integre seu checkout e comece a recuperar vendas perdidas
          </h2>
          <p className="text-slate-400 text-sm mt-2">
            Estado atual: {states[status?.state || "not_started"]}
          </p>
        </div>
        <div className="text-right shrink-0">
          <div className="font-heading text-3xl font-bold text-cp-primary-light tabular-nums">
            {percent}%
          </div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500">
            completo
          </div>
        </div>
      </div>
      <div className="h-1.5 bg-cp-elevated rounded-full overflow-hidden mb-6">
        <div
          className="h-full bg-cp-primary transition-all duration-500"
          style={{ width: `${percent}%` }}
        />
      </div>
      <div className="grid md:grid-cols-3 gap-3">
        {(status?.steps || []).map((step) => {
          const Icon = icons[step.key] || CheckCircle2;
          return (
            <Link
              key={step.key}
              to={step.cta || "/app/connection"}
              className={`flex items-center gap-3 p-4 rounded-lg border transition-all ${
                step.done
                  ? "bg-cp-primary/10 border-cp-primary/40 text-slate-100"
                  : "bg-cp-elevated/20 border-white/[0.06] hover:border-white/10 text-slate-200"
              }`}
            >
              <div
                className={`h-8 w-8 rounded-full shrink-0 flex items-center justify-center ${
                  step.done
                    ? "bg-cp-primary text-cp-bg"
                    : "bg-cp-elevated text-slate-400 border border-white/10"
                }`}
              >
                {step.done ? (
                  <CheckCircle2 className="h-4 w-4" />
                ) : (
                  <Icon className="h-4 w-4" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold">{step.label}</div>
                <div className="text-xs text-slate-500 truncate">
                  {subtitles[step.key]}
                </div>
              </div>
              {!step.done && (
                <ArrowRight className="h-4 w-4 text-slate-500 shrink-0" />
              )}
            </Link>
          );
        })}
      </div>
      <div className="mt-6 pt-6 border-t border-white/10 flex items-center justify-between flex-wrap gap-3">
        {status?.automation_active ? (
          <div className="text-sm text-cp-primary-light inline-flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4" /> Sua recuperacao automatica esta
            ativa.
          </div>
        ) : (
          <div className="text-xs text-slate-400">
            Ative o fluxo padrao para comecar a recuperar PIX pendente
            automaticamente.
          </div>
        )}
        <button
          type="button"
          onClick={onActivateRecovery}
          disabled={activating}
          className="cp-btn-primary px-4 py-2 text-sm disabled:opacity-40"
        >
          <Zap className="h-4 w-4" />{" "}
          {activating ? "Ativando..." : "Ativar Recuperacao Automatica"}
        </button>
      </div>
    </div>
  );
}

function AlertsPanel({ alerts = [] }) {
  if (!alerts.length) return null;
  const tone = {
    warning: "cp-alert-warning",
    error: "cp-alert-error",
    success: "cp-alert-success",
  };
  return (
    <div className="space-y-2">
      {alerts.map((a) => (
        <Link
          key={a.id}
          to={a.cta || "/app/dashboard"}
          className={`block ${tone[a.type] || tone.warning}`}
        >
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="font-semibold text-sm">{a.title}</div>
              <div className="text-xs opacity-90 mt-0.5">{a.message}</div>
            </div>
            <span className="text-[11px] underline">{a.cta_label || "Abrir"}</span>
          </div>
        </Link>
      ))}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const ctx = useOutletContext();
  const globalStatus = ctx?.status;
  const [metrics, setMetrics] = useState(null);
  const [insights, setInsights] = useState(null);
  const [period, setPeriod] = useState("30d");
  const [loading, setLoading] = useState(true);
  const [simOpen, setSimOpen] = useState(false);
  const [activating, setActivating] = useState(false);

  const load = useCallback(async () => {
    try {
      const [m, i] = await Promise.all([
        api.get("/dashboard/metrics"),
        api.get(`/dashboard/insights?period=${period}`),
      ]);
      setMetrics(m.data);
      setInsights(i.data);
    } finally {
      setLoading(false);
    }
  }, [period]);

  const activateRecovery = async () => {
    setActivating(true);
    try {
      await api.post("/automation/activate-default");
      toast.success("Recuperacao automatica ativada com sucesso.");
      await Promise.all([load(), ctx?.reloadStatus?.()]);
    } catch (e) {
      toast.error(
        e.response?.data?.detail ||
          "Nao foi possivel ativar a recuperacao automatica."
      );
    } finally {
      setActivating(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <WelcomeModal />
      <SimulateModal
        open={simOpen}
        onClose={() => setSimOpen(false)}
        onCreated={() => {
          load();
          ctx?.reloadStatus?.();
        }}
      />

      {/* ===== Header ===== */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tighter">
            Ola, {user?.name?.split(" ")[0] || "operador"} 👋
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            Painel de comando da operação. Tudo o que importa, em tempo real.
          </p>
        </div>
        <PeriodFilter value={period} onChange={setPeriod} />
      </div>

      <AlertsPanel alerts={globalStatus?.alerts || []} />

      {globalStatus && (
        <OnboardingChecklist
          status={globalStatus}
          onActivateRecovery={activateRecovery}
          activating={activating}
        />
      )}

      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="cp-panel h-32 animate-pulse" />
          ))}
        </div>
      ) : (
        <>
          {/* ===== HERO + Saúde ===== */}
          <div className="grid lg:grid-cols-3 gap-3">
            <div className="lg:col-span-2">
              <HeroKPI hero={insights?.hero} />
            </div>
            <div className="lg:col-span-1">
              <UpcomingJobs health={insights?.health} />
            </div>
          </div>

          <HealthPanel health={insights?.health} />

          {/* ===== KPI Cards (mantém os existentes) ===== */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            <Metric
              label="Valor recuperado hoje"
              value={formatCurrency(metrics?.value_recovered_today)}
              sub={
                metrics?.value_recovered_today > 0
                  ? "Dinheiro que voltou hoje"
                  : "Nenhuma recuperacao hoje"
              }
              icon={TrendingUp}
              accent="primary"
            />
            <Metric
              label="Taxa de recuperacao"
              value={`${(metrics?.recovery_rate || 0).toFixed(1)}%`}
              sub={`Sua taxa de recuperacao esta em ${(
                metrics?.recovery_rate || 0
              ).toFixed(1)}%`}
              icon={Target}
              accent="primary"
            />
            <Metric
              label="PIX recuperados"
              value={formatNumber(metrics?.total_recovered)}
              sub={`${formatCurrency(
                metrics?.value_recovered || 0
              )} no total`}
              icon={CheckCircle2}
              accent="primary"
            />
            <Metric
              label="PIX pendentes"
              value={formatNumber(metrics?.total_pending)}
              sub={`${formatCurrency(
                metrics?.value_pending || 0
              )} aguardando pagamento`}
              icon={Clock}
              accent="amber"
            />
            <Metric
              label="Estimativa de dinheiro perdido"
              value={formatCurrency(metrics?.value_lost_estimate)}
              sub="Pendentes e expirados dos ultimos 30 dias"
              icon={AlertTriangle}
              accent="amber"
            />
            <Metric
              label="Mensagens de hoje"
              value={formatNumber(insights?.messages_today?.sent || 0)}
              sub={`${insights?.messages_today?.failed || 0} falhas · ${
                insights?.messages_today?.skipped || 0
              } puladas`}
              icon={MessagesSquare}
              accent="zinc"
            />
          </div>

          {/* ===== FUNIL + STEP PERFORMANCE ===== */}
          <div className="grid lg:grid-cols-2 gap-3">
            <FunnelPanel funnel={insights?.funnel || []} />
            <StepPerformance steps={insights?.steps_perf || []} />
          </div>

          {/* ===== AVG METRICS ===== */}
          <AvgMetrics
            avgTimeSec={insights?.avg_time_to_pay_sec}
            avgTicketRecovered={insights?.avg_ticket_recovered}
            avgTicketLost={insights?.avg_ticket_lost}
          />

          {/* ===== TOP INTEGRATIONS / PRODUCTS / MESSAGES ===== */}
          <div className="grid lg:grid-cols-3 gap-3">
            <TopIntegrations items={insights?.top_integrations || []} />
            <TopProducts items={insights?.top_products || []} />
            <MessagesPanel
              today={insights?.messages_today}
              period={insights?.messages_period}
              deliveryRate={insights?.delivery_rate}
            />
          </div>

          {/* ===== HEATMAP + TIMESERIES CHART ===== */}
          <div className="grid lg:grid-cols-3 gap-3">
            <div className="lg:col-span-2 cp-panel p-5">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium mb-1">
                    ultimos 14 dias
                  </div>
                  <div className="font-heading text-lg font-semibold">
                    Recuperacao ao longo do tempo
                  </div>
                </div>
                <div className="flex gap-4 text-xs">
                  <span className="flex items-center gap-1.5">
                    <span className="h-2 w-2 rounded-full bg-cp-accent" />
                    Gerados
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="h-2 w-2 rounded-full bg-cp-primary-light" />
                    Recuperados
                  </span>
                </div>
              </div>
              <div className="h-60">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={metrics?.timeseries || []}>
                    <defs>
                      <linearGradient id="gGen" x1="0" y1="0" x2="0" y2="1">
                        <stop
                          offset="0%"
                          stopColor="#3B82F6"
                          stopOpacity={0.35}
                        />
                        <stop
                          offset="100%"
                          stopColor="#3B82F6"
                          stopOpacity={0}
                        />
                      </linearGradient>
                      <linearGradient id="gRec" x1="0" y1="0" x2="0" y2="1">
                        <stop
                          offset="0%"
                          stopColor="#22C55E"
                          stopOpacity={0.45}
                        />
                        <stop
                          offset="100%"
                          stopColor="#22C55E"
                          stopOpacity={0}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="rgba(255,255,255,0.06)"
                    />
                    <XAxis
                      dataKey="date"
                      stroke="#64748b"
                      tick={{ fontSize: 11 }}
                    />
                    <YAxis stroke="#64748b" tick={{ fontSize: 11 }} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#111827",
                        border: "1px solid rgba(255,255,255,0.08)",
                        borderRadius: 12,
                        fontSize: 12,
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="generated"
                      stroke="#3B82F6"
                      strokeWidth={2}
                      fill="url(#gGen)"
                    />
                    <Area
                      type="monotone"
                      dataKey="recovered"
                      stroke="#22C55E"
                      strokeWidth={2}
                      fill="url(#gRec)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="cp-panel p-5">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium mb-1">
                Ultima venda recuperada
              </div>
              {metrics?.last_recovered ? (
                <>
                  <div className="font-heading text-xl font-bold text-cp-primary-light">
                    {formatCurrency(metrics.last_recovered.amount)}
                  </div>
                  <div className="text-sm text-slate-300 mt-2">
                    Cliente: {metrics.last_recovered.customer_name || "-"}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    Ultima venda recuperada{" "}
                    {formatRelative(metrics.last_recovered.paid_at)}
                  </div>
                </>
              ) : (
                <div className="text-sm text-slate-500">
                  Ainda nao ha venda recuperada.
                </div>
              )}
              <div className="mt-5 pt-5 border-t border-white/[0.06] space-y-2">
                <button
                  type="button"
                  onClick={() => setSimOpen(true)}
                  data-testid="simulate-pix-btn"
                  className="cp-btn-secondary w-full py-2 text-sm"
                >
                  <Zap className="h-4 w-4 text-cp-warning" /> Simular PIX de teste
                </button>
                <Link
                  to="/app/chat"
                  className="cp-btn-secondary w-full py-2 text-sm"
                >
                  <MessagesSquare className="h-4 w-4 text-cp-primary-light" /> Ver
                  conversas
                </Link>
              </div>
            </div>
          </div>

          {/* ===== HEATMAP ===== */}
          <HeatmapPanel data={insights?.heatmap || []} />

          {/* ===== RECENT TRANSACTIONS ===== */}
          <RecentTransactions items={insights?.recent_transactions || []} />
        </>
      )}
    </div>
  );
}
